// Banner Card Height Matching
// Ensures all product cards have uniform height and banner cards match them

(function() {
  // Prevent multiple executions
  if (window.bannerCardHeightsInitialized) return;
  window.bannerCardHeightsInitialized = true;

let isCalculating = false;
let isScrolling = false;
let scrollTimeout;

// Track scrolling state
window.addEventListener('scroll', () => {
  isScrolling = true;
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    isScrolling = false;
  }, 150);
}, { passive: true });

function adjustBannerPositions() {
  const productLists = document.querySelectorAll('.product-list');
  
  productLists.forEach(list => {
    // Get all cards (products and banners) in order
    const allCards = Array.from(list.querySelectorAll('.product-card'));
    if (allCards.length === 0) return;
    
    // Calculate the number of columns in the grid
    const listWidth = list.offsetWidth;
    const firstCard = allCards.find(card => !card.classList.contains('banner-card'));
    if (!firstCard) return;
    
    const cardRect = firstCard.getBoundingClientRect();
    const cardWidth = cardRect.width;
    const columnGap = parseFloat(getComputedStyle(list).columnGap) || 0;
    
    // Calculate columns based on actual layout
    const columnsPerRow = Math.round((listWidth + columnGap) / (cardWidth + columnGap));
    
    if (columnsPerRow <= 2) {
      // Reset all adjustments for small layouts
      list.removeAttribute('data-explicit-grid');
      list.style.removeProperty('--explicit-grid-columns');
      allCards.forEach(card => {
        card.style.gridColumn = '';
        card.style.gridRow = '';
        card.classList.remove('banner-card--pushed');
      });
      return;
    }
    
    // Enable explicit grid mode
    list.setAttribute('data-explicit-grid', 'true');
    list.style.setProperty('--explicit-grid-columns', columnsPerRow);
    
    // Track which positions are filled in the grid
    const grid = {}; // grid[row][column] = true if filled
    
    // Helper to find next available position
    function findNextPosition(row, column, span) {
      while (true) {
        // Check if current position can fit the span
        let canFit = true;
        if (column + span - 1 > columnsPerRow) {
          canFit = false;
        } else {
          for (let c = column; c < column + span; c++) {
            if (grid[row] && grid[row][c]) {
              canFit = false;
              break;
            }
          }
        }
        
        if (canFit) {
          return { row, column };
        }
        
        // Move to next column
        column++;
        if (column > columnsPerRow) {
          row++;
          column = 1;
        }
      }
    }
    
    // Helper to mark positions as filled
    function markFilled(row, column, span) {
      if (!grid[row]) grid[row] = {};
      for (let c = column; c < column + span; c++) {
        grid[row][c] = true;
      }
    }
    
    // Position all cards - using dense packing algorithm
    allCards.forEach((card) => {
      // Reset any previous adjustments
      card.classList.remove('banner-card--pushed');
      
      // Determine card span
      const isMobile = window.innerWidth <= 699;
      let cardSpan = 1;
      
      if (isMobile) {
        if (card.classList.contains('banner-card--mobile-span-2')) {
          cardSpan = 2;
        }
      } else {
        if (card.classList.contains('banner-card--desktop-span-2')) {
          cardSpan = 2;
        }
      }
      
      // Always search from row 1, col 1 to find the first available position
      // This implements "dense" packing that fills gaps
      const pos = findNextPosition(1, 1, cardSpan);
      
      // Apply position
      card.style.gridColumn = `${pos.column} / span ${cardSpan}`;
      card.style.gridRow = pos.row;
      
      // Mark as filled
      markFilled(pos.row, pos.column, cardSpan);
      
      // Mark pushed banners
      if (cardSpan === 2 && pos.column === 1) {
        card.classList.add('banner-card--pushed');
      }
    });
  });
}

function matchCardHeights() {
  // Prevent concurrent calculations
  if (isCalculating) return;
  
  // Skip during active scrolling to prevent jumps
  if (isScrolling) {
    setTimeout(matchCardHeights, 200);
    return;
  }
  
  isCalculating = true;
  
  // First, adjust banner positions to prevent empty spaces
  adjustBannerPositions();
  
  const productLists = document.querySelectorAll('.product-list');
  
  productLists.forEach(list => {
    const productCards = list.querySelectorAll('.product-card:not(.banner-card)');
    const bannerCards = list.querySelectorAll('.banner-card');
    
    if (productCards.length === 0) {
      isCalculating = false;
      return;
    }
    
    // Check if elements have loaded dimensions
    const firstCard = productCards[0];
    if (!firstCard || firstCard.offsetHeight === 0) {
      // Elements not ready, try again after a short delay
      isCalculating = false;
      setTimeout(matchCardHeights, 50);
      return;
    }
    
    // Store current scroll position
    const scrollY = window.scrollY || window.pageYOffset;
    
    // Reset inline styles first
    productCards.forEach(card => {
      card.style.height = '';
    });
    bannerCards.forEach(banner => {
      const figure = banner.querySelector('.banner-card__figure');
      if (figure) figure.style.height = '';
    });
    
    // Find the tallest product card (image + info)
    let maxProductHeight = 0;
    let maxFigureHeight = 0;
    
    productCards.forEach(card => {
      const cardHeight = card.offsetHeight;
      const figure = card.querySelector('.product-card__figure');
      const figureHeight = figure ? figure.offsetHeight : 0;
      
      if (cardHeight > maxProductHeight) {
        maxProductHeight = cardHeight;
      }
      if (figureHeight > maxFigureHeight) {
        maxFigureHeight = figureHeight;
      }
    });
    
    // Only apply if heights changed to prevent layout shift
    let heightsChanged = false;
    
    // Apply uniform height to all product cards
    productCards.forEach(card => {
      const currentHeight = card.style.height;
      const newHeight = maxProductHeight + 'px';
      if (currentHeight !== newHeight) {
        card.style.height = newHeight;
        heightsChanged = true;
      }
    });
    
    // Apply heights to banner cards
    bannerCards.forEach(banner => {
      const figure = banner.querySelector('.banner-card__figure');
      if (!figure) return;
      
      // Check if mobile 2-column with square mode
      const isMobile = window.innerWidth <= 699;
      const isMobileTwoColumn = isMobile && banner.classList.contains('banner-card--mobile-span-2');
      const aspectMode = banner.getAttribute('data-mobile-aspect-mode');
      
      let newHeight;
      if (isMobileTwoColumn && aspectMode === 'square') {
        // For square mode, set height equal to width
        const bannerWidth = banner.offsetWidth;
        newHeight = bannerWidth + 'px';
      } else {
        // All other banners: match total product card height
        newHeight = maxProductHeight + 'px';
      }
      
      if (figure.style.height !== newHeight) {
        figure.style.height = newHeight;
        heightsChanged = true;
      }
    });
    
    // Restore scroll position to prevent jump
    requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - scrollY) > 5) {
        window.scrollTo(0, scrollY);
      }
    });
  });
  
  isCalculating = false;
}

// Run as soon as possible
function initBannerCards() {
  // Use requestAnimationFrame to ensure layout is calculated
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      matchCardHeights();
    });
  });
}

// Run on DOM ready or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBannerCards);
} else {
  initBannerCards();
}

// Run on window resize with debounce
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(matchCardHeights, 150);
});

// Run when images load
window.addEventListener('load', matchCardHeights);

// Handle pagination navigation
window.addEventListener('popstate', () => {
  setTimeout(matchCardHeights, 100);
});

// Handle Shopify section rendering
document.addEventListener('shopify:section:load', matchCardHeights);
document.addEventListener('shopify:section:reorder', matchCardHeights);

// Run when new content is added (for AJAX pagination)
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    let shouldRecalculate = false;
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.classList && (node.classList.contains('product-card') || node.classList.contains('banner-card'))) {
              shouldRecalculate = true;
            } else if (node.querySelector && (node.querySelector('.product-card') || node.querySelector('.banner-card'))) {
              shouldRecalculate = true;
            }
          }
        });
      }
    });
    if (shouldRecalculate) {
      setTimeout(matchCardHeights, 50);
    }
  });
  
  // Observe the entire body for content changes
  observer.observe(document.body, { childList: true, subtree: true });
  
  // Also observe product lists specifically
  setTimeout(() => {
    document.querySelectorAll('.product-list').forEach(list => {
      observer.observe(list, { childList: true, subtree: true });
    });
  }, 100);
}

})(); // End IIFE

